import dayjs, { DATE_FORMAT } from '@/libs/dayjs'
import { parseError } from '@/libs/error'
import i18n from '@/libs/i18n'
import {
    StaffTimeCorrectionState,
    StaffTimeEventKind,
    StaffTimeExcessState,
    StaffTimeSource,
} from '@/enums/staffTime'
import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { TagItem } from '@/types/Common'
import type { StaffResult } from '@/types/Staff'
import type {
    IStaffSettings,
    IStaffTimeCorrection,
    IStaffTimeCorrectionRequest,
    IStaffTimeDay,
    IStaffTimeEvent,
    IStaffTimeGrid,
    IStaffTimeMonthStatus,
    IStaffTimePerson,
    IStaffTimeRange,
    IStaffTimeStamp,
    IStaffTimeTotals,
} from '@/types/StaffTime'

/**
 * The working-time record, as one client (docs/OSEBJE.md §8, §9).
 *
 * Everything the four screens call lives here rather than in the screens, and that is
 * not tidiness: the personal screens read addresses OUTSIDE `/properties` and the
 * manager's screens read addresses inside it, and the difference is the whole
 * permission model. One file, so it stays visible which is which.
 *
 * The API contract is the one agreed for Del C (see `types/StaffTime.ts`). Where the
 * contract names a route but not a body, the reader below accepts both shapes it could
 * take and normalises them, which is said out loud at each place it happens.
 *
 * # Two things everything here obeys
 *
 * **A range has BOTH ENDS INCLUDED.** docs/EVIDENCA-CASA.md §2 fixes it for this record:
 * a week is asked for as the 7th to the 13th. It is the rota that has an exclusive `to`
 * (§20 of the same document says so where the two meet), and these readers used to
 * follow the rota — so every week and every month carried one day too many and summed
 * it.
 *
 * **A READ answers a StaffResult and never a bare `[]`.** An empty answer and a refused
 * one are the same value otherwise, and the screens drew „ni dni“ over both — see
 * `types/Staff.ts`.
 */

/**
 * One read, as a result: the body, or the service's own sentence about why there is none.
 *
 * A 200 with no body is data and not a fault — an endpoint answering `null` for "nothing
 * here" is answered with `empty`, which is what the caller wanted the empty answer to
 * mean in the first place.
 */
/**
 * A month with nothing in it, in one place.
 *
 * The panels each carried their own copy and one of them is what a failed read used to
 * be shown as. It is exported so that a screen resetting itself and a reader answering
 * an empty month cannot drift apart.
 */
export const EMPTY_STAFF_TOTALS: IStaffTimeTotals = {
    worked_minutes:   0,
    break_minutes:    0,
    expected_minutes: 0,
    absence_minutes:  0,
    balance_minutes:  0,
}

const staffResultOf = <T>(statusCode: number, body: T | null, empty: T): StaffResult<T> => {
    if (statusCode === 200) return { ok: true, data: body ?? empty }

    return { ok: false, error: parseError(body, i18n.global.t('errors.on_load')) }
}

/** The IANA zone this browser is in. It travels with every stamp — §8 says why. */
export const deviceZone = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Ljubljana'
    } catch {
        // A browser that will not say. The house's own zone is a better guess than
        // none, and the server holds the house's setting anyway.
        return 'Europe/Ljubljana'
    }
}

/**
 * A press, named before it is sent, so that pressing again is one stamp and not two.
 *
 * CLAUDE.md invariant 5, and §8 states it again for this record in particular: two
 * presses are two stamps and one press delivered twice is one. The id and the INSTANT
 * are both minted at the press — a retry that re-read the clock would move the stamp
 * to whenever the network came back, which is the quietest possible way to keep a wrong
 * record: every stamp looks right and every one of them is late.
 */
export const newStamp = (kind: string): IStaffTimeStamp => ({
    operation_id: crypto.randomUUID(),
    kind,
    occurred_at:  new Date().toISOString(),
    zone:         deviceZone(),
})

/**
 * Minutes as hours and minutes: `450` → `7 h 30 min`.
 *
 * The same function `libs/staff.ts` has, re-exported rather than re-written, so a
 * balance on the rota and a balance on the time record are printed by one rule.
 */
export { formatMinutes } from '@/libs/staff'

/** Today, as the record's own day key. */
export const todayKey = (): string => dayjs().format(DATE_FORMAT)

/** The Monday of this week, and the first of this month, as `YYYY-MM-DD`. */
export const monthStart = (date: string): string => dayjs(date).startOf('month').format(DATE_FORMAT)
export const monthEnd   = (date: string): string => dayjs(date).endOf('month').format(DATE_FORMAT)

/**
 * What an entry MEANS, as against what its `kind` field says.
 *
 * An approved correction is appended as `corrected_time_event`, and what the record now
 * reads is in `corrected_kind` (§9) — so anything that folds entries into a state has to
 * come through here. A fold that switched on `kind` alone would drop every corrected
 * arrival, and the day would quietly say somebody never came in.
 *
 * Mirrors `StaffTimeEventEntry.EffectiveKind` on the API side, deliberately to the
 * letter: two spellings of one rule is the bug this shape exists to prevent.
 */
export const effectiveKind = (event: IStaffTimeEvent): string => {
    const kind = event.kind === StaffTimeEventKind.Corrected ? (event.corrected_kind ?? '') : event.kind

    switch (kind) {
        case StaffTimeEventKind.ClockIn:
        case StaffTimeEventKind.ClockOut:
        case StaffTimeEventKind.BreakStart:
        case StaffTimeEventKind.BreakEnd:
            return kind
        default:
            return ''
    }
}

/**
 * Whether an entry is part of the arithmetic at all.
 *
 * A SUPERSEDED entry is not: the correction that replaced it carries what the record now
 * holds, and counting both would count one arrival twice. It stays in `events` and stays
 * readable — §9 marks the original, never strikes it through — and this is only the line
 * between what is READ and what is COUNTED. Mirrors
 * `StaffTimeEventEntry.CountsTowardsDay`.
 */
export const countsTowardsDay = (event: IStaffTimeEvent): boolean =>
    !event.corrected && effectiveKind(event) !== ''

/**
 * What the person may press right now, given today's stamps.
 *
 * Read from the events rather than from a state field, because there is no state field:
 * the day is a calculation over an append-only list (§8), and a screen that kept its
 * own idea of "clocked in" would be a second source of truth for the one fact this
 * record exists to hold.
 *
 * Two decisions worth naming:
 *
 *   - a day that has been clocked OUT offers a fresh arrival. Split working time is in
 *     the law's own list of things this record has to be able to say (§8), and a person
 *     who comes back at six has to be able to say so.
 *   - while a break is open, the departure is still offered. It is not the button the
 *     screen leads with — ending the break is — but a person who forgot to press "konec
 *     odmora" and is now walking out must be able to record that she is walking out.
 *     Inventing a break end for her would be writing a fact nobody witnessed, which is
 *     the one thing §8 refuses outright.
 */
export const allowedKinds = (day: IStaffTimeDay | null, breakRecording: boolean): string[] => {
    const events = day?.events ?? []

    let clockedIn = false
    let onBreak   = false

    for (const event of events) {
        if (!countsTowardsDay(event)) continue

        const kind = effectiveKind(event)

        if (kind === StaffTimeEventKind.ClockIn)    { clockedIn = true;  onBreak = false }
        if (kind === StaffTimeEventKind.ClockOut)   { clockedIn = false; onBreak = false }
        if (kind === StaffTimeEventKind.BreakStart) onBreak = true
        if (kind === StaffTimeEventKind.BreakEnd)   onBreak = false
    }

    if (!clockedIn) return [StaffTimeEventKind.ClockIn]

    if (onBreak) return [StaffTimeEventKind.BreakEnd, StaffTimeEventKind.ClockOut]

    return breakRecording
        ? [StaffTimeEventKind.BreakStart, StaffTimeEventKind.ClockOut]
        : [StaffTimeEventKind.ClockOut]
}

/**
 * Whether a day's excess is still waiting for somebody to classify it (§8).
 *
 * One value, because the API writes one: `awaiting_classification` on a day over the
 * house's threshold and the empty string otherwise. Classifying an excess is phase 3,
 * so there is nothing else for a day to be yet.
 */
export const awaitsClassification = (day: IStaffTimeDay): boolean =>
    day.excess_state === StaffTimeExcessState.AwaitingClassification

/** The word for one press. */
export const eventKindLabel = (kind: string): string => {
    const map: Record<string, string> = {
        [StaffTimeEventKind.ClockIn]:    i18n.global.t('staff_time.kind_clock_in'),
        [StaffTimeEventKind.ClockOut]:   i18n.global.t('staff_time.kind_clock_out'),
        [StaffTimeEventKind.BreakStart]: i18n.global.t('staff_time.kind_break_start'),
        [StaffTimeEventKind.BreakEnd]:   i18n.global.t('staff_time.kind_break_end'),
    }

    return map[kind] ?? kind
}

/** The icon for one press. Four buttons on a telephone read faster with a glyph on them. */
export const eventKindIcon = (kind: string): string => {
    const map: Record<string, string> = {
        [StaffTimeEventKind.ClockIn]:    'i-lucide-log-in',
        [StaffTimeEventKind.ClockOut]:   'i-lucide-log-out',
        [StaffTimeEventKind.BreakStart]: 'i-lucide-coffee',
        [StaffTimeEventKind.BreakEnd]:   'i-lucide-play',
    }

    return map[kind] ?? 'i-lucide-clock'
}

/** Where a stamp came from, in words. On the drill-down it is a column, never a hint. */
export const sourceLabel = (source: string): string => {
    const map: Record<string, string> = {
        [StaffTimeSource.PersonalDevice]: i18n.global.t('staff_time.source_personal_device'),
        [StaffTimeSource.Kiosk]:          i18n.global.t('staff_time.source_kiosk'),
        [StaffTimeSource.Manual]:         i18n.global.t('staff_time.source_manual'),
    }

    return map[source] ?? source
}

/** A correction's state as a badge. A refusal is red because it is a refusal (§9). */
export const correctionState = (state: string): TagItem => {
    const map: Record<string, TagItem> = {
        [StaffTimeCorrectionState.Requested]: { label: i18n.global.t('staff_time.correction_requested'), color: 'warning' },
        [StaffTimeCorrectionState.Approved]:  { label: i18n.global.t('staff_time.correction_approved'),  color: 'success' },
        [StaffTimeCorrectionState.Rejected]:  { label: i18n.global.t('staff_time.correction_rejected'),  color: 'error' },
    }

    return map[state] ?? { label: state, color: 'neutral' }
}

// MARK: - The personal reads. No property, no permission: the API narrows to the
// signed-in account, and that narrowing is the guard rather than anything on a screen.

/** One's own days over a range. BOTH ENDS INCLUDED — see the file comment. */
export const loadMyTime = async (from: string, to: string): Promise<StaffResult<IStaffTimeRange>> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/staff/me/time?from=${from}&to=${to}`)
        .get()
        .json<IStaffTimeRange>()

    return staffResultOf(statusCode.value ?? 0, data.value, { days: [], totals: { ...EMPTY_STAFF_TOTALS } })
}

/**
 * One press. Answers the created stamp, or throws the body so the screen can say why.
 *
 * `replay` in the answer means the service already had this operation id and did not
 * make a second stamp — which is the retry working, not a failure, and the screen says
 * nothing different about it.
 */
export const sendMyStamp = async (stamp: IStaffTimeStamp): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch('/staff/me/time-events')
        .post(stamp)
        .json<{ id: string, replay: boolean }>()

    return { ok: response.value?.status === 201, body: data.value }
}

/** One's own corrections, requested and decided alike — a refusal stays visible (§9). */
export const loadMyCorrections = async (): Promise<StaffResult<IStaffTimeCorrection[]>> => {
    const { statusCode, data } = await useFetchStore()
        .fetch('/staff/me/time-corrections')
        .get()
        .json<IStaffTimeCorrection[]>()

    return staffResultOf(statusCode.value ?? 0, data.value, [])
}

/** Asking for a correction. Anybody may ask; nobody decides their own (§9). */
export const requestMyCorrection = async (
    request: IStaffTimeCorrectionRequest,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch('/staff/me/time-corrections')
        .post(request)
        .json<IStaffTimeCorrection>()

    return { ok: response.value?.status === 201, body: data.value }
}

/** One's own kiosk PIN. Write-only, like every other secret in this product. */
export const setMyKioskPIN = async (pin: string): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch('/me/kiosk-pin')
        .put({ pin })
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

// MARK: - The house-wide reads. Inside /properties, behind staff:time and staff:approve.

/** The house's own two settings for this record. */
export const loadStaffSettings = async (propertyId: string): Promise<IStaffSettings | null> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/settings`)
        .get()
        .json<IStaffSettings>()

    return statusCode.value === 200 ? (data.value ?? null) : null
}

export const saveStaffSettings = async (
    propertyId: string,
    settings: IStaffSettings,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/settings`)
        .put(settings)
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

/**
 * The grid: every person's days over a period.
 *
 * The API answers ONE shape — `{days, totals}`, the same document the person's own read
 * answers, because §13 makes self-service the other side of being in the record rather
 * than a smaller view of it. The rows are grouped here by the `user_id` each day
 * carries; there is no pre-grouped alternative and the screen must not look for one.
 */
export const loadStaffTimeGrid = async (
    propertyId: string,
    query: string,
): Promise<StaffResult<IStaffTimePerson[]>> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/time?${query}`)
        .get()
        .json<IStaffTimeGrid>()

    if (statusCode.value !== 200) {
        return { ok: false, error: parseError(data.value, i18n.global.t('errors.on_load')) }
    }

    return { ok: true, data: groupByPerson(data.value?.days ?? []) }
}

/**
 * The unfinished days, which are the first thing a manager has to deal with (§8).
 *
 * A bare array, and it takes no range: the window is fixed at 62 days on the server so
 * that nobody can ask for one that happens to leave a day out, and today is never in it
 * — everybody at work right now has an open day.
 */
export const loadOpenDays = async (propertyId: string): Promise<StaffResult<IStaffTimeDay[]>> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/time/open-days`)
        .get()
        .json<IStaffTimeDay[]>()

    return staffResultOf(statusCode.value ?? 0, data.value, [])
}

export const loadCorrections = async (
    propertyId: string,
    state: string,
): Promise<IStaffTimeCorrection[]> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/time-corrections${state ? `?state=${state}` : ''}`)
        .get()
        .json<IStaffTimeCorrection[]>()

    if (statusCode.value !== 200 || !data.value) return []

    return data.value
}

/**
 * Deciding a correction. The API refuses a decision on one's own request and this is
 * NOT where that rule lives — the screen only reflects it (§9).
 */
export const decideCorrection = async (
    propertyId: string,
    id: string,
    approve: boolean,
    reason: string,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/time-corrections/${id}/${approve ? 'approve' : 'reject'}`)
        .post({ reason })
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

// MARK: - Closing a month (docs/EVIDENCA-CASA.md § „Zaključevanje meseca“).

/**
 * The month as a path segment, "2026-09".
 *
 * One segment and not two, because the routes read as somebody says them. Built here
 * rather than with `dayjs().format('YYYY-MM')` at four call sites, so that the screen and
 * the address cannot disagree about which September they mean.
 */
export const staffTimeMonthKey = (year: number, month: number): string =>
    `${year}-${String(month).padStart(2, '0')}`

/**
 * Where one month stands: open, closed at a version with a hash, or reopened with the
 * reason somebody gave.
 *
 * A `StaffResult`, like every read on this screen, and for the reason v1.501.0 gave: a
 * refused or broken read drawn as an OPEN month would offer a Zaključi button on a month
 * that is already signed off, and the desk would learn about it from the refusal.
 */
export const loadStaffTimeMonthStatus = async (
    propertyId: string,
    year: number,
    month: number,
): Promise<StaffResult<IStaffTimeMonthStatus | null>> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/time/months/${staffTimeMonthKey(year, month)}`)
        .get()
        .json<IStaffTimeMonthStatus>()

    return staffResultOf(statusCode.value ?? 0, data.value, null)
}

/**
 * Signing a month off. `staff:time` or `staff:manage`.
 *
 * The three refusals — the month has not ended, something inside it is still waiting to
 * be decided, it is already closed — arrive as the server's own sentences and are shown
 * verbatim (`libs/error.ts`: the server's words win by default). The pending one also
 * carries the counts in `details`, which is what lets the dialog say how many.
 */
export const closeStaffTimeMonth = async (
    propertyId: string,
    year: number,
    month: number,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/time/months/${staffTimeMonthKey(year, month)}/close`)
        .post({})
        .json()

    return { ok: response.value?.status === 201, body: data.value }
}

/**
 * Taking that signature back. `staff:manage`, and a REASON that is required.
 *
 * Required because this is the one act in the working-time record somebody outside the
 * house asks about, and an act nobody has to explain is an act nobody can answer. The
 * dialog refuses a blank before this is called; the server refuses one too.
 */
export const reopenStaffTimeMonth = async (
    propertyId: string,
    year: number,
    month: number,
    reason: string,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/time/months/${staffTimeMonthKey(year, month)}/reopen`)
        .post({ reason })
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

/** Somebody else's kiosk PIN, set by a manager. Digits only, never read back. */
export const setKioskPIN = async (userId: string, pin: string): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/users/${userId}/kiosk-pin`)
        .post({ pin })
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

/**
 * Whether the account looking at the screen is the one who asked (§9).
 *
 * Used to hide the decide buttons on one's own request and to say why they are absent.
 * The API refuses it regardless — this is a sentence, not a guard.
 */
export const isOwnRequest = (correction: IStaffTimeCorrection): boolean => {
    const me = useAppStore().userData?.id ?? ''

    return !!me && (correction.requested_by === me || correction.user_id === me)
}

/** Groups a flat day list by the person each day names. See `loadStaffTimeGrid`. */
const groupByPerson = (days: IStaffTimeDay[]): IStaffTimePerson[] => {
    const people = new Map<string, IStaffTimePerson>()

    for (const day of days) {
        const id = day.user_id ?? ''

        let person = people.get(id)

        if (!person) {
            person = {
                user_id:         id,
                user_name:       day.user_name ?? i18n.global.t('staff.unknown_person'),
                department_id:   day.department_id,
                department_name: day.department_name,
                days:            [],
                totals:          {
                    worked_minutes:   0,
                    break_minutes:    0,
                    expected_minutes: 0,
                    absence_minutes:  0,
                    balance_minutes:  0,
                },
            }

            people.set(id, person)
        }

        person.days?.push(day)

        person.totals.worked_minutes   += day.worked_minutes
        person.totals.break_minutes    += day.break_minutes
        person.totals.expected_minutes += day.expected_minutes ?? 0
        person.totals.absence_minutes  += day.absence?.minutes ?? 0
        person.totals.balance_minutes  += day.balance_minutes ?? 0
    }

    return [...people.values()].sort((a, b) => a.user_name.localeCompare(b.user_name))
}
